import { createSelector } from "@reduxjs/toolkit";
import djs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import durationP from "dayjs/plugin/duration";
import isTdP from "dayjs/plugin/isToday";
import isTmrP from "dayjs/plugin/isTomorrow";
import tzP from "dayjs/plugin/timezone";
import utcP from "dayjs/plugin/utc";
import { __ } from "../http/helpers/translates.js";
import { BASE_TIMEZONE, BASE_LOCALE, ISO_FORMAT } from "./date-settings.js";
djs.extend(tzP);
djs.extend(utcP);
djs.extend(isTdP);
djs.extend(isTmrP);
djs.extend(customParseFormat);
djs.extend(durationP);
const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const isString = (el) => typeof el === "string";
const toDate = (d) => d.toDate();
const toValidDateFormat = (date) => isString(date) ? date.replace(/\./g, "-").replace(/\//g, "-").replace(/\\/g, "-").replace(/(\d) (\d)/g, "$1T$2").replace(/ /g, "") : date;
const FORMATS = [
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "DD-MM-YYYY",
  "YYYY/MM/DD",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD HH:mmZ",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm Z",
  "YYYY-MM-DDTHH:mmZ",
  "YYYY-MM-DDTHH:mmZZ",
  "YYYY-MM-DDTHH:mm[Z]",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DDTHH:mm:ssZZ",
  "YYYY-MM-DDTHH:mm:ss[Z]",
  "YYYY-MM-DDTHH:mm:ss Z",
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
  "YYYY-MM-DDTHH:mm:ss.SSSZZ",
  "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
  "YYYY-MM-DDTHH:mm:ss.SSS Z",
  "YYYY-MM-DD'T'HH:mmZ",
  "YYYY-MM-DD'T'HH:mmZZ",
  "YYYY-MM-DD'T'HH:mm Z",
  "YYYY-MM-DD'T'HH:mm[Z]",
  "YYYY-MM-DD'T'HH:mm:ssZ",
  "YYYY-MM-DD'T'HH:mm:ssZZ",
  "YYYY-MM-DD'T'HH:mm:ss[Z]",
  "YYYY-MM-DD'T'HH:mm:ss Z",
  "YYYY-MM-DD'T'HH:mm:ss.SSSZ",
  "YYYY-MM-DD'T'HH:mm:ss.SSS Z",
  "YYYY-MM-DDTHH:mm:ss[Z]",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DDTHH:mm:ssZZ"
];
const isValidDate = (date) => {
  if (date.isValid !== null && typeof date.isValid === "function") return date.isValid();
  if (isString(date)) return djs(date, FORMATS, true).isValid();
  return djs(date).isValid();
};
const toValidDate = (date, returnInvalid, tz) => {
  if (date == null) return djs();
  if (djs.isDayjs(date) && isValidDate(date)) return date;
  if (typeof date === "number" && !isNaN(date)) {
    return isValidDate(date) ? djs.tz(date, tz || BASE_TIMEZONE) : djs();
  }
  if (isString(date)) {
    const parsedDate = toValidDateFormat(date);
    return isValidDate(parsedDate) ? djs.tz(parsedDate, tz || BASE_TIMEZONE) : djs();
  }
  if (isValidDate(date) || returnInvalid) return djs.tz(date, tz || BASE_TIMEZONE);
  return djs();
};
const getToday = () => djs();
const checkIsToday = (date) => {
  return toValidDate(date).isToday();
};
const checkIsTomorrow = (date) => {
  return toValidDate(date).isTomorrow();
};
const getDurationFromMap = (duration) => Object.entries(duration);
const addToDate = (date, duration) => getDurationFromMap(duration).reduce(
  (acc, [unit, value]) => acc.add(value, unit),
  toValidDate(date)
).toDate();
const subFromDate = (date, duration) => getDurationFromMap(duration).reduce(
  (acc, [unit, value]) => acc.subtract(value, unit),
  toValidDate(date)
).toDate();
const getEndOfDay = (date, tz) => {
  return toValidDate(date, null, tz).endOf("day").toDate();
};
const getStartOfDay = (date, tz) => {
  return toValidDate(date, null, tz).startOf("day").toDate();
};
const getMonth = (date) => toValidDate(date).get("month");
const getDate = (date) => toValidDate(date).get("date");
const getDiffInYears = (d1, d2) => djs(toValidDate(d1)).diff(toValidDate(d2), "years");
const isDateInFuture = (date) => toValidDate(date).isAfter(getToday());
const getStringAsDateTz = (dateString, options) => {
  const dateFormatted = toValidDateFormat(dateString);
  const timestamp = Date.parse(dateFormatted);
  if (dateFormatted && !isNaN(timestamp)) {
    return (options == null ? void 0 : options.timeZone) ? djs(timestamp).tz(options.timeZone) : djs.utc(dateFormatted);
  }
  return null;
};
const getUtcSecondsFromDiffInDays = (day) => djs().subtract(day, "day").unix();
const utcToZonedTime = (date, tz, { timestamp, format, toNative, keepLocalTime } = {}) => {
  const d = keepLocalTime ? toValidDate(date).utc(true).tz(tz || BASE_TIMEZONE) : djs.utc(toValidDate(date)).tz(tz || BASE_TIMEZONE);
  if (toNative) return toDate(d);
  if (format) return d.format(format);
  if (timestamp) return d.valueOf();
  return d;
};
const zonedTimeToUtc = (date, tz, { timestamp, format, toNative } = {}) => {
  const d = toValidDate(date, null, tz).utc();
  if (toNative) return toDate(d);
  if (format) return d.format(format);
  if (timestamp) return d.valueOf();
  return d;
};
const daysOffsetMinMax = (date1, date2) => toValidDate(date1).diff(toValidDate(date2), "days");
const daysOffset = (date1, date2, number) => {
  const d2 = toValidDate(date2);
  d2.set("year", d2.get("year") - number);
  return daysOffsetMinMax(date1, d2);
};
const dateTimeConverter = (date, formatS, tz) => {
  return djs.tz(toValidDate(date), tz || djs.tz.guess()).format(formatS || ISO_FORMAT);
};
const dateToUtc = (date, timeZone, todayStart, todayEnd) => {
  if (!date) {
    return date;
  }
  const tz = timeZone || "Europe/London";
  if (todayStart) {
    const startDay = getStartOfDay(date, tz);
    return Math.floor(zonedTimeToUtc(startDay, tz, { timestamp: true }) / 1e3);
  }
  if (todayEnd) {
    const endDay = getEndOfDay(date, tz);
    return Math.floor(zonedTimeToUtc(endDay, tz, { timestamp: true }) / 1e3);
  }
  return Math.floor(zonedTimeToUtc(date, tz, { timestamp: true }) / 1e3);
};
const dateToCustomerZone = createSelector(
  (date) => date,
  (date, tz) => tz || djs.tz.guess(),
  (date, tz) => utcToZonedTime(date, tz)
);
const getDifferenceBetweenDateAndNow = (date, options = {
  timeZone: BASE_TIMEZONE,
  locales: BASE_LOCALE
}) => {
  const { timeZone } = options;
  return djs(zonedTimeToUtc(date, timeZone)).diff(Date.now(), "milliseconds");
};
const getDifferenceBetweenNowAndDate = (date, options = {
  timeZone: BASE_TIMEZONE,
  locales: BASE_LOCALE
}) => {
  const { timeZone } = options;
  return djs(djs().tz(timeZone)).diff(zonedTimeToUtc(date, timeZone), "milliseconds");
};
const getDifferenceBetweenTwoDates = (date, dateToCompare, options = {
  timeZone: BASE_TIMEZONE,
  locales: BASE_LOCALE
}) => {
  const { timeZone } = options;
  return djs(zonedTimeToUtc(date, timeZone)).diff(zonedTimeToUtc(dateToCompare, timeZone), "milliseconds");
};
const getUnitsFromTs = (ts) => {
  const tsInSeconds = Math.floor(ts / 1e3);
  const hours = Math.floor(tsInSeconds / 60 / 60);
  const minutes = Math.floor(tsInSeconds / 60) - hours * 60;
  const seconds = tsInSeconds % 60;
  return {
    hours,
    minutes,
    seconds
  };
};
const formatTimeUnits = ({
  hours,
  minutes,
  seconds
}) => `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
const formatRelativeDayString = (date, format, tz) => {
  const zoneD = toValidDate(date);
  if (checkIsToday(zoneD)) return __("Default_._Relative Day_._Today");
  if (checkIsTomorrow(zoneD)) return __("Default_._Relative Day_._Tomorrow");
  return dateTimeConverter(date, format, tz);
};
const formatDate = (date, format, options = {}) => {
  const o = {
    locale: djs.locale(),
    ...options
  };
  if (o.timezone) {
    return djs.tz(date, o.timezone).locale(o == null ? void 0 : o.locale).format(format);
  }
  return djs(date).locale(o == null ? void 0 : o.locale).format(format);
};
const concatDateString = (year, month, day) => {
  const withZero = (n) => n < 10 ? `0${n}` : n;
  return `${year}-${withZero(Number(month))}-${withZero(Number(day))}`;
};
const addIsoPeriodToDate = (date, period) => {
  const formattedPeriod = djs.duration(period);
  return djs(date).add(formattedPeriod);
};
const addCurrentTimeMilliseconds = (baseDateInput = 0) => {
  const baseDate = djs(baseDateInput);
  const now = djs();
  const updatedDate = baseDate.hour(now.hour()).minute(now.minute()).second(now.second()).millisecond(now.millisecond());
  return updatedDate.valueOf();
};
export {
  LOCAL_TIMEZONE,
  addCurrentTimeMilliseconds,
  addIsoPeriodToDate,
  addToDate,
  checkIsToday,
  checkIsTomorrow,
  concatDateString,
  dateTimeConverter,
  dateToCustomerZone,
  dateToUtc,
  daysOffset,
  daysOffsetMinMax,
  formatDate,
  formatRelativeDayString,
  formatTimeUnits,
  getDate,
  getDiffInYears,
  getDifferenceBetweenDateAndNow,
  getDifferenceBetweenNowAndDate,
  getDifferenceBetweenTwoDates,
  getEndOfDay,
  getMonth,
  getStartOfDay,
  getStringAsDateTz,
  getToday,
  getUnitsFromTs,
  getUtcSecondsFromDiffInDays,
  isDateInFuture,
  isValidDate,
  subFromDate,
  toDate,
  toValidDate,
  toValidDateFormat,
  utcToZonedTime,
  zonedTimeToUtc
};
